import {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getSigninFormValidationSchema,
  signinFormDefaultValue,
  signinFormType,
} from './utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { UPasswordInput } from 'components/core/u-password-input';
import { Captcha } from 'features/captcha';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useToast } from 'hooks/use-toast';
import { useTheme } from 'components/core/theme-provider';
import { useSigninMutation } from '../../hooks/use-auth';
import ErrorAlert from '../../../../components/blocks/error-alert/error-alert';
import { SignInResponse } from '../../services/auth-service';
import { SsoSignin } from 'pages/auth/signin/signin-sso';
import { GRANT_TYPES } from 'constant/auth';
import { LoginOption } from 'constant/sso';
import { useConfig } from 'hooks/use-config';
import { GDPRConsentDialog } from 'components/blocks/gdpr-consent-dialog';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';
import { SigninOidc } from 'pages/auth/signin/signin-oidc';
import { AuthDivider } from 'pages/auth/shared/auth-divider';
import { PoweredByBa } from 'pages/auth/shared/powered-by-ba';
import { KlaxWelcome } from 'components/blocks/klax-welcome';
import { cn } from 'lib/utils';
import { useCaptchaConfig } from '../../hooks/use-captcha-config';
import {
  buildVerifyKeyPath,
  getMfaChallengeFromError,
  getMfaChallengeFromLoginResult,
} from 'features/auth/utils/mfa-challenge';


/**
 * SigninForm Component
 *
 * A comprehensive login form component that handles user authentication with email and password,
 * supports two-factor authentication flow, and implements CAPTCHA protection after failed attempts.
 *
 * Features:
 * - Email and password authentication
 * - Form validation with Zod schema
 * - Progressive security with CAPTCHA after 3 failed login attempts
 * - Two-factor authentication (2FA/MFA) integration
 * - Error handling with alert display
 * - Navigation to relevant routes based on authentication status
 * - Success notification via toast
 * - "Forgot password" link
 *
 * @returns {JSX.Element} The rendered login form with validation and security features
 *
 * @example
 * // Basic usage
 * <SigninForm />
 *
 * // Within an authentication page
 * <div className="auth-container">
 *   <h1>Welcome Back</h1>
 *   <SigninForm />
 *   <div className="auth-footer">
 *     <p>Don't have an account? <Link to="/signup">Sign up</Link></p>
 *   </div>
 * </div>
 */

type SigninProps = {
  loginOption?: LoginOption;
  variant?: 'default' | 'klax';
  signUpEnabled?: boolean;
};

export const SigninForm = ({ loginOption, variant = 'default', signUpEnabled = false }: SigninProps) => {
  const [searchParams] = useSearchParams();
  const isFirstTimeCall = useRef<boolean>(true);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuthStore();
  const { toast } = useToast();
  const { resolvedTheme } = useTheme();
  const captchaTheme = resolvedTheme === 'dark' ? 'dark' : 'light';

  // Helper function to handle navigation after successful login
  const navigateAfterLogin = () => {
    const gotoParam = searchParams.get('goto');
    if (gotoParam) {
      navigate(gotoParam, { replace: true });
    } else {
      navigate('/chat', { replace: true });
    }
  };
  const [captchaToken, setCaptchaToken] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [showGDPRDialog, setShowGDPRDialog] = useState(false);
  const [isFinalizingLogin, setIsFinalizingLogin] = useState(false);
  const [isSsoCallbackProcessing, setIsSsoCallbackProcessing] = useState(false);
  const { configurationSettings } = useConfig();
  const { data: captchaConfig, isLoading: isCaptchaConfigLoading } = useCaptchaConfig();

  const isCaptchaEnabled = Boolean(
    captchaConfig?.captcha && Object.keys(captchaConfig.captcha).length > 0
  );

  const form = useForm({
    defaultValues: signinFormDefaultValue,
    resolver: zodResolver(getSigninFormValidationSchema(t)),
  });

  const [username, password] = form.watch(['username', 'password']);
  const isCredentialsEmpty = !username.trim() || !password.trim();

  const { isPending, mutateAsync, isError, errorDetails } = useSigninMutation();
  const isLoginInProgress = isPending || isFinalizingLogin;
  const isFormDisabled = isLoginInProgress || isSsoCallbackProcessing;

  const handleCaptchaVerify = (token: SetStateAction<string>) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken('');
  };

  const finalizeLogin = async (res: SignInResponse) => {
    setIsFinalizingLogin(true);
    try {
      login('', '');
    } finally {
      setIsFinalizingLogin(false);
    }
  };

  const handleGDPRConsent = async () => {
    setShowGDPRDialog(false);

    try {
      // Get the current form values
      const values = form.getValues();

      // Retry the login process with terms and conditions accepted
      const res = (await mutateAsync({
        grantType: 'password',
        username: values.username.toLowerCase(),
        password: values.password,
        termsAndConditionsAccepted: true,
        ...(captchaToken && { captcha_code: captchaToken }),
      })) as SignInResponse;

      const mfaChallenge = getMfaChallengeFromLoginResult(res);
      if (mfaChallenge) {
        navigate(buildVerifyKeyPath(mfaChallenge.mfaId, mfaChallenge.mfaType, values.username));
      } else {
        await finalizeLogin(res);
        setTimeout(() => {
          navigateAfterLogin();
          toast({
            variant: 'success',
            title: t('SUCCESS'),
            description: t('LOGIN_SUCCESSFULLY'),
          });
        }, 100);
      }
    } catch (error: any) {
      const mfaChallenge = getMfaChallengeFromError(error);
      if (mfaChallenge) {
        const username = form.getValues().username;
        navigate(buildVerifyKeyPath(mfaChallenge.mfaId, mfaChallenge.mfaType, username));
        return;
      }

      console.error('Failed to login with consent:', error?.error.error);
      if (error?.error.error === 'invalid_username_password') {
        toast({
          variant: 'destructive',
          title: t('INVALID_CREDENTIALS', 'Login Failed'),
          description: t('EMAIL_PASSWORD_NOT_VALID', 'Failed to complete login. Please try again.'),
        });
      } else {
        toast({
          variant: 'destructive',
          title: t('GDPR_CONSENT_ERROR', 'Login Failed'),
          description: t('GDPR_CONSENT_ERROR_DESCRIPTION', 'Failed to complete login. Please try again.'),
        });
      }
    }
  };

  const handleGDPRDecline = () => {
    setShowGDPRDialog(false);
    toast({
      variant: 'destructive',
      title: t('USER_NOT_AGREED_TERMS_AND_CONDITIONS'),
      description: t('USER_NOT_AGREED_TERMS_AND_CONDITIONS_DESCRIPTION'),
    });
  };

  const onSubmitHandler = async (values: signinFormType) => {
    if (isCaptchaEnabled && showCaptcha && !captchaToken) {
      return;
    }
    trackEvent(EVENTS.FORM_SUBMITTED("Signin Form Submitted"));
    try {
      useAuthStore.getState().setIsOIDCLogin(false);

      const res = (await mutateAsync({
        grantType: 'password',
        username: values.username.toLowerCase(),
        password: values.password,
        ...(captchaToken && { captcha_code: captchaToken }),
      })) as SignInResponse;

      const mfaChallenge = getMfaChallengeFromLoginResult(res);
      if (mfaChallenge) {
        navigate(buildVerifyKeyPath(mfaChallenge.mfaId, mfaChallenge.mfaType, values.username));
      } else {
        await finalizeLogin(res);
        setTimeout(() => {
          navigateAfterLogin();
          toast({
            variant: 'success',
            title: t('SUCCESS'),
            description: t('LOGIN_SUCCESSFULLY'),
          });
        }, 100);
      }
    } catch (_error: any) {
      const mfaChallenge = getMfaChallengeFromError(_error);
      if (mfaChallenge) {
        navigate(buildVerifyKeyPath(mfaChallenge.mfaId, mfaChallenge.mfaType, values.username));
        return;
      }

      if (_error.error.error === 'user_not_agreed_terms_and_conditions') {
        setShowGDPRDialog(true);
        return; // Don't increment failed attempts for consent issues
      }

      // Check if the API is requiring captcha
      if (_error.error.error === 'captcha_enabled' && _error.error.captcha_required) {
        setShowCaptcha(true);
        return; // Don't increment failed attempts when API requires captcha
      }

      // Check if account is locked - error will be displayed by ErrorAlert
      if (_error.error.error === 'account_locked') {
        return; // Don't increment failed attempts when account is locked
      }

      if (isCaptchaEnabled) {
        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);

        if (newFailedAttempts >= 3 && !showCaptcha) {
          setShowCaptcha(true);
        }
      }
    }
  };

  const passwordGrantAllowed = loginOption?.allowedGrantTypes?.includes(GRANT_TYPES.password);
  const socialGrantAllowed = loginOption?.allowedGrantTypes?.includes(GRANT_TYPES.social);
  const oidcGrantAllowed = loginOption?.allowedGrantTypes?.includes(GRANT_TYPES.oidc);
  const isKlax = variant === 'klax';

  const klaxInputClassName =
    '!h-[54px] rounded-xl border-[#d1d1d1] px-5 !text-base font-normal leading-[1.6] text-black shadow-none placeholder:text-[#888888] focus-visible:ring-0 dark:border-[#3d3d3d] dark:bg-[#19191c] dark:text-true-white dark:placeholder:text-[#b0b0b0]';
  const klaxPasswordInputClassName = `${klaxInputClassName} pr-12`;
  const klaxLabelClassName = 'text-base font-medium leading-[1.2] text-black dark:text-true-white';
  const klaxFieldClassName = 'space-y-2.5';

  const signin = useCallback(
    async (code: string, state: string) => {
      setIsSsoCallbackProcessing(true);
      try {
        const provider = localStorage.getItem('sso_provider') || 'microsoft';
        await mutateAsync({ grantType: 'social', code, state, provider });
        localStorage.removeItem('sso_provider');
        login('', '');
        // Check if there's a goto parameter in the URL
        const gotoParam = searchParams.get('goto');
        if (gotoParam) {
          navigate(gotoParam);
        } else {
          navigate('/chat');
        }
        toast({
          variant: 'success',
          title: t('SUCCESS'),
          description: t('LOGIN_SUCCESSFULLY'),
        });
      } catch (error) {
        // Error will be handled by the ErrorAlert component through errorDetails from useSigninMutation
        console.error('SSO signin error:', error);
        // Reset loading state immediately
        setIsSsoCallbackProcessing(false);
        // Remove code and state from URL to prevent infinite processing
        navigate('/login', { replace: true });
      } finally {
        setIsSsoCallbackProcessing(false);
      }
    },
    [login, mutateAsync, navigate, searchParams, t, toast]
  );

  useEffect(() => {
    const state = searchParams.get('state');
    const code = searchParams.get('code');
    if (code && state && isFirstTimeCall.current) {
      isFirstTimeCall.current = false;
      signin(code, state);
    }
  }, [searchParams, signin]);

  // Reset firstTimeCall flag when URL changes to allow retry
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    if (!code && !state) {
      isFirstTimeCall.current = true;
    }
  }, [searchParams]);

  if (isSsoCallbackProcessing) {
    return (
      <div className="flex w-full items-center justify-center">
        <KlaxWelcome
          title={t('REDIRECTING_PLEASE_WAIT', 'Redirecting... Please wait')}
          className="w-full min-w-0"
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      <ErrorAlert isError={isError} title={errorDetails.title} message={errorDetails.message} />

      {isKlax ? (
        <div className="flex w-full flex-col gap-5 sm:gap-7">
          {socialGrantAllowed && loginOption && (
            <SsoSignin loginOption={loginOption} variant="klax" showDivider={true} />
          )}

          {/* {socialGrantAllowed && loginOption && (
            <AuthDivider text={t('AUTH_OR', 'OR')} variant="klax" />
          )} */}

          {passwordGrantAllowed && (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmitHandler)} className="flex w-full flex-col gap-8 sm:gap-10">
                <div className="flex w-full flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                      <FormItem className={klaxFieldClassName}>
                        <FormLabel className={klaxLabelClassName}>{t('EMAIL')}</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder={t('ENTER_YOUR_EMAIL_ADDRESS', 'Enter your email address')}
                            className={klaxInputClassName}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem className={`w-full ${klaxFieldClassName}`}>
                        <FormLabel className={klaxLabelClassName}>{t('PASSWORD')}</FormLabel>
                        <FormControl>
                          <UPasswordInput
                            type="password"
                            placeholder={t('ENTER_YOUR_PASSWORD', 'Enter your password')}
                            className={klaxPasswordInputClassName}
                            {...field}
                          />
                        </FormControl>
                        <div className="grid w-full grid-cols-[1fr_auto] items-start gap-x-3">
                          <FormMessage className="col-start-1 min-w-0 text-sm leading-[1.2]" />
                          <Link
                            to="/forgot-password"
                            className="col-start-2 row-start-1 shrink-0 text-base font-medium leading-[1.2] text-black hover:underline dark:text-true-white"
                          >
                            {t('FORGOT_PASSWORD', 'Forgot Password?')}
                          </Link>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex w-full flex-col items-center gap-6">
                  {isCaptchaEnabled && showCaptcha && captchaConfig?.captcha && (
                    <Captcha
                      type="reCaptcha"
                      siteKey={captchaConfig.captcha.key}
                      theme={captchaTheme}
                      onVerify={handleCaptchaVerify}
                      onExpired={handleCaptchaExpired}
                      size="normal"
                    />
                  )}

                  <Button
                    type="submit"
                    className={cn(
                      'flex h-[48px] w-full items-center justify-center gap-2 rounded-full px-10 py-3 text-sm font-medium leading-[1.2] sm:gap-[12px] sm:h-[48px] sm:text-base',
                      isCredentialsEmpty &&
                      'border-[#D1D1D1] bg-[#D1D1D1] text-[#6D6D6D] hover:bg-[#D1D1D1] hover:text-[#6D6D6D] disabled:opacity-100 dark:border-[#6D6D6D] dark:bg-[#6D6D6D] dark:text-[#D1D1D1] dark:hover:bg-[#6D6D6D] dark:hover:text-[#D1D1D1]'
                    )}
                    disabled={
                      isLoginInProgress ||
                      isCaptchaConfigLoading ||
                      isCredentialsEmpty ||
                      (isCaptchaEnabled && showCaptcha && !captchaToken)
                    }
                    loading={isLoginInProgress}
                  >
                    {isLoginInProgress ? t('LOGGING_YOU_IN', 'Logging you in...') : t('LOG_IN', 'Login')}
                  </Button>

                  <PoweredByBa />

                  {signUpEnabled && (
                    <p className="text-center text-base leading-[1.6] text-[#5d5d5d] dark:text-[#e7e7e7]">
                      {t('DONT_HAVE_AN_ACCOUNT', "Don't have an account?")}{' '}
                      <Link to="/signup" className="font-semibold leading-[1.2] text-black hover:underline dark:text-true-white">
                        {t('SIGN_UP', 'Sign up')}
                      </Link>
                    </p>
                  )}
                </div>
              </form>
            </Form>
          )}

          {oidcGrantAllowed && loginOption && <SigninOidc loginOption={loginOption} />}
        </div>
      ) : (
        <>
          {passwordGrantAllowed && (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmitHandler)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm">{t('EMAIL')}</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder={t('ENTER_YOUR_EMAIL')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="text-sm">
                      <FormLabel>{t('PASSWORD')}</FormLabel>
                      <FormControl>
                        <UPasswordInput type="password" placeholder={t('ENTER_YOUR_PASSWORD')} {...field} />
                      </FormControl>
                      <div className="grid grid-cols-[1fr_auto] items-start gap-x-2">
                        <FormMessage className="col-start-1 min-w-0" />
                        <Link
                          to="/forgot-password"
                          className="col-start-2 row-start-1 shrink-0 text-sm text-primary hover:text-primary-600 hover:underline"
                        >
                          {t('FORGOT_PASSWORD')}
                        </Link>
                      </div>
                    </FormItem>
                  )}
                />

                {isCaptchaEnabled && showCaptcha && captchaConfig?.captcha && (
                  <div className="my-4">
                    <Captcha
                      type="reCaptcha"
                      siteKey={captchaConfig.captcha.key}
                      theme={captchaTheme}
                      onVerify={handleCaptchaVerify}
                      onExpired={handleCaptchaExpired}
                      size="normal"
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  className="flex h-[50px] w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium leading-[1.2] sm:h-[54px] sm:gap-[12px] sm:px-10 sm:py-3.5 sm:text-base"
                  disabled={
                    isLoginInProgress ||
                    isCaptchaConfigLoading ||
                    isCredentialsEmpty ||
                    (isCaptchaEnabled && showCaptcha && !captchaToken)
                  }
                  loading={isLoginInProgress}
                >
                  {isLoginInProgress ? t('LOGGING_YOU_IN', 'Logging you in...') : t('LOG_IN')}
                </Button>

                <PoweredByBa />
              </form>
            </Form>
          )}

          {socialGrantAllowed && loginOption && <SsoSignin loginOption={loginOption} />}
          {!socialGrantAllowed && oidcGrantAllowed && (
            <div className="mt-4">
              <AuthDivider text={t('AUTH_OR')} />
            </div>
          )}
          {oidcGrantAllowed && loginOption && <SigninOidc loginOption={loginOption} />}
        </>
      )}


      {/* GDPR Consent Dialog */}
      <GDPRConsentDialog
        open={showGDPRDialog}
        onOpenChange={setShowGDPRDialog}
        onConsent={handleGDPRConsent}
        onDecline={handleGDPRDecline}
      />
    </div>
  );
};
